import { useState, useRef, useEffect } from 'preact/hooks'
import { ChevronDown, Building2, Check } from 'lucide-preact'
import { THEME_COLORS } from '../../constants/theme'
import type { ClientWorkspaceSummaryViewDto } from '@/types/company'

interface WorkspaceSelectorProps {
  selectedWorkspaceId?: string
  onWorkspaceChange: (workspace: ClientWorkspaceSummaryViewDto) => void
  workspaces: ClientWorkspaceSummaryViewDto[] | null | undefined
}

export function WorkspaceSelector({ selectedWorkspaceId, onWorkspaceChange, workspaces }: WorkspaceSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!workspaces || workspaces.length === 0) {
    return (
      <div className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">
        No workspaces available
      </div>
    )
  }

  // If only one workspace, show it as a simple label
  if (workspaces.length === 1) {
    const workspace = workspaces[0]
    return (
      <div
        className="flex items-center px-3 py-2 rounded-lg border sm:min-w-60 max-w-full min-w-0"
        style={{
          backgroundColor: THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE,
          borderColor: THEME_COLORS.BORDERS.LIGHT
        }}
      >
        <Building2 className="w-4 h-4 mr-2" style={{ color: THEME_COLORS.GREEN[500] }} />
        <span className="font-medium truncate text-sm" style={{ color: THEME_COLORS.GREEN[600] }}>
          {workspace.Name}
        </span>
      </div>
    )
  }

  // Multiple workspaces - show dropdown selector
  const selectedWorkspace = workspaces.find(w => w.Id === selectedWorkspaceId) || workspaces[0]

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between px-2 sm:px-3 py-2 text-sm rounded-lg transition-colors duration-200 sm:min-w-60 w-full border hover:opacity-90"
        style={{
          backgroundColor: THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE,
          borderColor: THEME_COLORS.BORDERS.LIGHT
        }}
      >
        <div className="flex items-center min-w-0">
          <Building2 className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: THEME_COLORS.GREEN[500] }} />
          <span className="font-medium truncate" style={{ color: THEME_COLORS.GREEN[600] }}>
            {selectedWorkspace.Name}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
            }`}
          style={{ color: THEME_COLORS.GREEN[500] }}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 sm:left-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto min-w-48 sm:min-w-60">
          {workspaces.map((workspace) => {
            const isSelected = workspace.Id === selectedWorkspace.Id
            return (
              <button
                key={workspace.Id}
                onClick={() => {
                  onWorkspaceChange(workspace)
                  setIsOpen(false)
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg hover:bg-slate-50 dark:hover:bg-slate-700`}
                style={{
                  backgroundColor: isSelected ? THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE : 'transparent'
                }}
              >
                <div className="flex items-center min-w-0">
                  <Building2
                    className={`w-4 h-4 mr-2 flex-shrink-0 ${isSelected ? '' : 'text-slate-400 dark:text-slate-500'
                      }`}
                    style={{ color: isSelected ? THEME_COLORS.GREEN[500] : undefined }}
                  />
                  <div className="text-left min-w-0">
                    <div
                      className={`font-medium truncate ${isSelected ? '' : 'text-slate-700 dark:text-slate-300'
                        }`}
                      style={{ color: isSelected ? THEME_COLORS.GREEN[600] : undefined }}
                    >
                      {workspace.Name}
                    </div>
                  </div>
                </div>
                {isSelected && (
                  <Check className="w-4 h-4 flex-shrink-0" style={{ color: THEME_COLORS.GREEN[500] }} />
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
